import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { bonusAdminEnvelope } from "./bonusAdminBody";
import { getSegmentQueryKey } from "./useGetSegment";
import { LIST_SEGMENTS_QUERY_KEY } from "./useListSegments";
import type { SegmentIn, SegmentOut } from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

type UpdateSegmentVars = { segmentId: string; payload: SegmentIn };

type UpdateSegmentResult = { body: SegmentOut; message?: string };

async function putSegment({ segmentId, payload }: UpdateSegmentVars): Promise<UpdateSegmentResult> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.UPDATE_DYNAMIC_BONUS_SEGMENT.replace("{segment_id}", segmentId);
  const response = await instance.put(url, payload);
  const envelope = bonusAdminEnvelope<SegmentOut>(response);
  if (!envelope.body) {
    throw new Error("Segment update returned no data");
  }
  return { body: envelope.body, message: envelope.message };
}

export const useUpdateSegment = (segmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation<UpdateSegmentResult, Error, SegmentIn>({
    mutationFn: (payload) => putSegment({ segmentId, payload }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: LIST_SEGMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: getSegmentQueryKey(segmentId) });
      message.success(result.message || AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED);
    },
    onError: (error) => {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    },
  });
};
